import React, { useState } from 'react';
import Breadcrumb from '../components/Breadcrumb';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Calculator, TrendingDown, TrendingUp } from 'lucide-react';
import SEO, { generateBreadcrumbSchema } from '../components/SEO';
import AdSense from '../components/AdSense';

const MathRisk = () => {
  // CSP Calculator State
  const [cspStrike, setCspStrike] = useState(50);
  const [cspPremium, setCspPremium] = useState(2);
  const [cspStockPrice, setCspStockPrice] = useState(48);

  // CC Calculator State
  const [ccCostBasis, setCcCostBasis] = useState(45);
  const [ccStrike, setCcStrike] = useState(50);
  const [ccPremium, setCcPremium] = useState(2);
  const [ccStockPrice, setCcStockPrice] = useState(52);

  // CSP Calculations
  const cspBreakeven = cspStrike - cspPremium;
  const cspMaxProfit = cspPremium;
  const cspMaxLoss = cspStrike - cspPremium;
  const cspCapitalRequired = cspStrike * 100;
  const cspCurrentPL = cspStockPrice >= cspStrike 
    ? cspPremium 
    : (cspStockPrice - cspStrike + cspPremium);

  // CC Calculations
  const ccNewBasis = ccCostBasis - ccPremium;
  const ccMaxProfit = (ccStrike - ccCostBasis) + ccPremium;
  const ccMaxLoss = ccCostBasis - ccPremium;
  const ccCurrentPL = ccStockPrice <= ccStrike
    ? (ccStockPrice - ccCostBasis + ccPremium)
    : (ccStrike - ccCostBasis + ccPremium);

  return (
    <div className="min-h-screen">
      <SEO
        title="Options Calculator | Premium & Risk Analysis Tools"
        description="Free interactive options calculators for CSP and covered calls. Calculate max profit, breakeven, risk, and analyze scenarios in real-time."
        keywords="options calculator, options profit calculator, CSP calculator, covered call calculator, options risk analysis, breakeven calculator"
        canonical="https://optionsanalogies.com/math-risk"
        type="website"
        schema={[
          generateBreadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Math & Risk Calculators", path: "/math-risk" }
          ])
        ]}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Breadcrumb items={[{ label: 'Math & Risk', path: '/math-risk' }]} />

        {/* Header - Ad-Free Zone */}
        <div className="mb-12" data-nosnippet>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">Math & Risk</h1>
          <p className="text-xl text-gray-300">
            Interactive calculators and payoff scenarios to understand options mechanics and risk.
          </p>
        </div>

        {/* Disclaimer */}
        <Alert className="mb-8 border-amber-500/50 bg-amber-500/10">
          <AlertDescription className="text-gray-300">
            <strong className="text-white">Educational Tool Only:</strong> These calculators demonstrate options mechanics. They do not account for commissions, taxes, early assignment, or real-time market conditions. Not trading advice.
          </AlertDescription>
        </Alert>

        {/* Interactive Calculators - Ad-Free Zone */}
        <Tabs defaultValue="csp" className="mb-12" data-nosnippet>
          <TabsList className="bg-gray-900/50 border border-gray-800">
            <TabsTrigger value="csp" className="data-[state=active]:bg-emerald-600">
              CSP Calculator
            </TabsTrigger>
            <TabsTrigger value="cc" className="data-[state=active]:bg-emerald-600">
              CC Calculator
            </TabsTrigger>
          </TabsList>

          {/* CSP Calculator */}
          <TabsContent value="csp" className="mt-8">
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Inputs */}
              <Card className="bg-gray-900/50 border-gray-800">
                <CardHeader>
                  <CardTitle className="text-white flex items-center">
                    <Calculator className="w-5 h-5 mr-2 text-emerald-500" />
                    Cash-Secured Put Inputs
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <Label className="text-gray-300 mb-3 block">Strike Price</Label>
                    <div className="flex items-center gap-4 mb-2">
                      <input
                        type="number"
                        value={cspStrike}
                        onChange={(e) => setCspStrike(Math.max(0, Math.min(1000, Number(e.target.value))))}
                        className="w-24 px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-emerald-400 font-mono text-center focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        min="0"
                        max="1000"
                        step="1"
                      />
                      <div className="flex-1">
                        <Slider
                          value={[cspStrike]}
                          onValueChange={(val) => setCspStrike(val[0])}
                          min={0}
                          max={1000}
                          step={1}
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label className="text-gray-300 mb-3 block">Premium Collected</Label>
                    <div className="flex items-center gap-4 mb-2">
                      <input
                        type="number"
                        value={cspPremium}
                        onChange={(e) => setCspPremium(Math.max(0, Math.min(1000, Number(e.target.value))))}
                        className="w-24 px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-emerald-400 font-mono text-center focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        min="0"
                        max="1000"
                        step="0.5"
                      />
                      <div className="flex-1">
                        <Slider
                          value={[cspPremium]}
                          onValueChange={(val) => setCspPremium(val[0])}
                          min={0}
                          max={1000}
                          step={0.5}
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label className="text-gray-300 mb-3 block">Current Stock Price</Label>
                    <div className="flex items-center gap-4 mb-2">
                      <input
                        type="number"
                        value={cspStockPrice}
                        onChange={(e) => setCspStockPrice(Math.max(0, Math.min(1000, Number(e.target.value))))}
                        className="w-24 px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-emerald-400 font-mono text-center focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        min="0"
                        max="1000"
                        step="1"
                      />
                      <div className="flex-1">
                        <Slider
                          value={[cspStockPrice]}
                          onValueChange={(val) => setCspStockPrice(val[0])}
                          min={0}
                          max={1000}
                          step={1}
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Results */}
              <Card className="bg-gray-900/50 border-gray-800">
                <CardHeader>
                  <CardTitle className="text-white">Results & Analysis</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-emerald-500/10 p-4 rounded-lg">
                      <p className="text-sm text-gray-400 mb-1">Max Profit</p>
                      <p className="text-2xl font-bold text-emerald-400">${cspMaxProfit.toFixed(2)}</p>
                    </div>
                    <div className="bg-blue-500/10 p-4 rounded-lg">
                      <p className="text-sm text-gray-400 mb-1">Breakeven</p>
                      <p className="text-2xl font-bold text-blue-400">${cspBreakeven.toFixed(2)}</p>
                    </div>
                    <div className="bg-red-500/10 p-4 rounded-lg">
                      <p className="text-sm text-gray-400 mb-1">Max Loss</p>
                      <p className="text-2xl font-bold text-red-400">${cspMaxLoss.toFixed(2)}</p>
                    </div>
                    <div className="bg-purple-500/10 p-4 rounded-lg">
                      <p className="text-sm text-gray-400 mb-1">Capital Required</p>
                      <p className="text-2xl font-bold text-purple-400">${cspCapitalRequired}</p>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-gray-700">
                    <p className="text-sm text-gray-400 mb-2">Current P/L (per share)</p>
                    <div className={`text-3xl font-bold flex items-center ${
                      cspCurrentPL >= 0 ? 'text-emerald-400' : 'text-red-400'
                    }`}>
                      {cspCurrentPL >= 0 ? <TrendingUp className="w-6 h-6 mr-2" /> : <TrendingDown className="w-6 h-6 mr-2" />}
                      ${cspCurrentPL.toFixed(2)}
                    </div>
                  </div>

                  <div className="pt-4 border-t border-gray-700">
                    <p className="text-sm font-semibold text-gray-300 mb-2">Status</p>
                    <p className="text-gray-400">
                      {cspStockPrice >= cspStrike 
                        ? '✅ Stock above strike → Option expires → Keep premium'
                        : '⚠️ Stock below strike → Assignment likely → Buy at $' + cspStrike}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* CC Calculator */}
          <TabsContent value="cc" className="mt-8">
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Inputs */}
              <Card className="bg-gray-900/50 border-gray-800">
                <CardHeader>
                  <CardTitle className="text-white flex items-center">
                    <Calculator className="w-5 h-5 mr-2 text-emerald-500" />
                    Covered Call Inputs
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <Label className="text-gray-300 mb-3 block">Your Cost Basis</Label>
                    <div className="flex items-center gap-4 mb-2">
                      <input
                        type="number"
                        value={ccCostBasis}
                        onChange={(e) => setCcCostBasis(Math.max(0, Math.min(1000, Number(e.target.value))))}
                        className="w-24 px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-emerald-400 font-mono text-center focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        min="0"
                        max="1000"
                        step="1"
                      />
                      <div className="flex-1">
                        <Slider
                          value={[ccCostBasis]}
                          onValueChange={(val) => setCcCostBasis(val[0])}
                          min={0}
                          max={1000}
                          step={1}
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label className="text-gray-300 mb-3 block">Strike Price</Label>
                    <div className="flex items-center gap-4 mb-2">
                      <input
                        type="number"
                        value={ccStrike}
                        onChange={(e) => setCcStrike(Math.max(0, Math.min(1000, Number(e.target.value))))}
                        className="w-24 px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-emerald-400 font-mono text-center focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        min="0"
                        max="1000"
                        step="1"
                      />
                      <div className="flex-1">
                        <Slider
                          value={[ccStrike]}
                          onValueChange={(val) => setCcStrike(val[0])}
                          min={0}
                          max={1000}
                          step={1}
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label className="text-gray-300 mb-3 block">Premium Collected</Label>
                    <div className="flex items-center gap-4 mb-2">
                      <input
                        type="number"
                        value={ccPremium}
                        onChange={(e) => setCcPremium(Math.max(0, Math.min(1000, Number(e.target.value))))}
                        className="w-24 px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-emerald-400 font-mono text-center focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        min="0"
                        max="1000"
                        step="0.5"
                      />
                      <div className="flex-1">
                        <Slider
                          value={[ccPremium]}
                          onValueChange={(val) => setCcPremium(val[0])}
                          min={0}
                          max={1000}
                          step={0.5}
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label className="text-gray-300 mb-3 block">Current Stock Price</Label>
                    <div className="flex items-center gap-4 mb-2">
                      <input
                        type="number"
                        value={ccStockPrice}
                        onChange={(e) => setCcStockPrice(Math.max(0, Math.min(1000, Number(e.target.value))))}
                        className="w-24 px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-emerald-400 font-mono text-center focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        min="0"
                        max="1000"
                        step="1"
                      />
                      <div className="flex-1">
                        <Slider
                          value={[ccStockPrice]}
                          onValueChange={(val) => setCcStockPrice(val[0])}
                          min={0}
                          max={1000}
                          step={1}
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Results */}
              <Card className="bg-gray-900/50 border-gray-800">
                <CardHeader>
                  <CardTitle className="text-white">Results & Analysis</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-emerald-500/10 p-4 rounded-lg">
                      <p className="text-sm text-gray-400 mb-1">Max Profit</p>
                      <p className="text-2xl font-bold text-emerald-400">${ccMaxProfit.toFixed(2)}</p>
                    </div>
                    <div className="bg-blue-500/10 p-4 rounded-lg">
                      <p className="text-sm text-gray-400 mb-1">New Basis</p>
                      <p className="text-2xl font-bold text-blue-400">${ccNewBasis.toFixed(2)}</p>
                    </div>
                    <div className="bg-red-500/10 p-4 rounded-lg col-span-2">
                      <p className="text-sm text-gray-400 mb-1">Max Loss (if stock → $0)</p>
                      <p className="text-2xl font-bold text-red-400">${ccMaxLoss.toFixed(2)}</p>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-gray-700">
                    <p className="text-sm text-gray-400 mb-2">Current P/L (per share)</p>
                    <div className={`text-3xl font-bold flex items-center ${
                      ccCurrentPL >= 0 ? 'text-emerald-400' : 'text-red-400'
                    }`}>
                      {ccCurrentPL >= 0 ? <TrendingUp className="w-6 h-6 mr-2" /> : <TrendingDown className="w-6 h-6 mr-2" />}
                      ${ccCurrentPL.toFixed(2)}
                    </div>
                  </div>

                  <div className="pt-4 border-t border-gray-700">
                    <p className="text-sm font-semibold text-gray-300 mb-2">Status</p>
                    <p className="text-gray-400">
                      {ccStockPrice <= ccStrike 
                        ? '✅ Stock below strike → Option expires → Keep shares + premium'
                        : '⚠️ Stock above strike → Assignment likely → Shares called at $' + ccStrike}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>

        {/* Ad Placement 1 - After Calculators */}
        <section className="mb-12">
          <AdSense slot="9979062833" format="rectangle" />
        </section>

        {/* Assignment Scenarios Table */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-white mb-6">Assignment Scenarios</h2>
          <div className="overflow-x-auto">
            <table className="w-full glass rounded-lg overflow-hidden">
              <thead className="bg-gray-800/50">
                <tr>
                  <th className="text-left p-4 text-gray-300 font-semibold">Strategy</th>
                  <th className="text-left p-4 text-gray-300 font-semibold">Stock Price</th>
                  <th className="text-left p-4 text-gray-300 font-semibold">Outcome</th>
                  <th className="text-left p-4 text-gray-300 font-semibold">Result</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                <tr className="hover:bg-gray-800/30 transition">
                  <td className="p-4 text-white font-medium">CSP</td>
                  <td className="p-4 text-gray-300">Well below strike</td>
                  <td className="p-4 text-gray-300">Assigned</td>
                  <td className="p-4 text-red-400">Buy shares at strike (above market)</td>
                </tr>
                <tr className="hover:bg-gray-800/30 transition">
                  <td className="p-4 text-white font-medium">CSP</td>
                  <td className="p-4 text-gray-300">Near strike</td>
                  <td className="p-4 text-gray-300">May assign</td>
                  <td className="p-4 text-yellow-400">Uncertain outcome</td>
                </tr>
                <tr className="hover:bg-gray-800/30 transition">
                  <td className="p-4 text-white font-medium">CSP</td>
                  <td className="p-4 text-gray-300">Well above strike</td>
                  <td className="p-4 text-gray-300">Expires</td>
                  <td className="p-4 text-emerald-400">Keep premium, no shares</td>
                </tr>
                <tr className="hover:bg-gray-800/30 transition">
                  <td className="p-4 text-white font-medium">CC</td>
                  <td className="p-4 text-gray-300">Well below strike</td>
                  <td className="p-4 text-gray-300">Expires</td>
                  <td className="p-4 text-emerald-400">Keep shares + premium</td>
                </tr>
                <tr className="hover:bg-gray-800/30 transition">
                  <td className="p-4 text-white font-medium">CC</td>
                  <td className="p-4 text-gray-300">Near strike</td>
                  <td className="p-4 text-gray-300">May assign</td>
                  <td className="p-4 text-yellow-400">Uncertain outcome</td>
                </tr>
                <tr className="hover:bg-gray-800/30 transition">
                  <td className="p-4 text-white font-medium">CC</td>
                  <td className="p-4 text-gray-300">Well above strike</td>
                  <td className="p-4 text-gray-300">Assigned</td>
                  <td className="p-4 text-amber-400">Shares called at strike (capped gains)</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Ad Placement 2 - After Scenarios */}
        <section className="mb-12">
          <AdSense slot="9113521962" format="horizontal" />
        </section>
      </div>
    </div>
  );
};

export default MathRisk;